/**
 * The tenant admins' Licences page and the seat check at sign-in (docs/specs/cloud/licences-page.md; decisions 290,
 * 295, 447, 838). Reads the tenant licence, its open seats and devices and Braynewave's invoice records through their
 * policies (tenant admins of their own tenant only), releases through licence_end and licence_seat_end, and asks
 * licence_web_checkin at sign-in. The database decides everything; this file only asks.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  accountLabel,
  isPilotOnly,
  LICENCE_WEB_CHECK_KEY,
  parseWebCheckinAnswer,
  webCheckOn,
  type LicenceDeviceKind,
  type WebCheckinAnswer,
} from '@teslyn/domain';
import {
  adminError,
  endLicence,
  invoiceFromRow,
  loadMinSyncVersion,
  loadRecommendedVersion,
  type Invoice,
  type Licence,
} from '../admin/api.ts';

export interface LicenceDevice {
  readonly id: string;
  readonly kind: LicenceDeviceKind;
  /** What the device reported (20261028150000); null for Teslyn Field up to 2026.10.12 and before the migration. */
  readonly platform: string | null;
  readonly label: string;
  readonly activatedAt: string | null;
  readonly lastCheckinAt: string | null;
  /** The Teslyn Field (or phone app) version at its last check-in. */
  readonly appVersion: string | null;
}

export interface LicenceSeat {
  readonly id: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string | null;
  readonly takenAt: string;
  readonly lastCheckinAt: string | null;
  readonly lastWebAt: string | null;
  readonly devices: readonly LicenceDevice[];
}

export interface LicencesPage {
  readonly licence: Licence | null;
  readonly seats: readonly LicenceSeat[];
  readonly invoices: readonly Invoice[];
  /** Whether the seat check at sign-in is on in this environment (platform setting `licence_web_check`). */
  readonly webCheck: boolean;
  readonly minSyncVersion: string | null;
  readonly recommendedVersion: string | null;
  /** Sign-ins whose only role here is Pilot (decision 918): their seat rows are not counted against the licence. */
  readonly pilotOnly: ReadonlySet<string>;
}

type UserEmbed = { readonly email: string; readonly display_name: string; readonly system?: boolean } | null;
const one = <T>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? (v[0] ?? null) : (v ?? null));
const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0));
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function fail(error: { message: string; code?: string } | null): void {
  if (error) throw new Error(adminError(error));
}

/** Whether the seat check at sign-in is on; off when the row is missing (the migration is not applied). */
export async function loadWebCheck(sb: SupabaseClient): Promise<boolean> {
  const { data, error } = await sb
    .from('platform_setting')
    .select('key, value')
    .eq('key', LICENCE_WEB_CHECK_KEY)
    .maybeSingle();
  if (error) return false;
  return webCheckOn(str((data as Record<string, unknown> | null)?.value));
}

/** Braynewave's console: switch the seat check at sign-in on or off. Platform admins at two-step sign-in; audited. */
export async function saveWebCheck(sb: SupabaseClient, on: boolean, note: string): Promise<void> {
  const { data, error } = await sb
    .from('platform_setting')
    .update({ value: on ? 'on' : null, note })
    .eq('key', LICENCE_WEB_CHECK_KEY)
    .select('key');
  fail(error);
  if (!data || data.length === 0)
    throw new Error(
      'Not saved: the database has no licence check setting yet (20261028150000 is not applied), or refused the change.',
    );
}

export async function loadLicencesPage(sb: SupabaseClient, tenantId: string): Promise<LicencesPage> {
  const [lic, seats, devices, invoices, webCheck, minimum, recommended, members] = await Promise.all([
    sb.from('tenant_licence').select('*').eq('tenant_id', tenantId).maybeSingle(),
    sb
      .from('licence_seat')
      .select(
        'id, user_id, taken_at, last_checkin_at, last_web_at, app_user:user_id (email, display_name, system)',
      )
      .eq('tenant_id', tenantId)
      .is('ended_at', null)
      .order('taken_at', { ascending: true }),
    // '*': device_platform comes with 20261028150000; the page also works before it is applied
    sb.from('licence_activation').select('*').eq('tenant_id', tenantId).is('ended_at', null),
    sb.from('invoice_record').select('*').eq('tenant_id', tenantId).order('issued_on', { ascending: false }),
    loadWebCheck(sb),
    loadMinSyncVersion(sb).catch(() => null),
    loadRecommendedVersion(sb).catch(() => null),
    // who is a pilot only (decision 918): tenant admins read their organisation's memberships
    sb.from('membership').select('user_id, role').eq('tenant_id', tenantId),
  ]);
  for (const r of [lic, seats, devices, invoices, members]) fail(r.error);
  const rolesOf = new Map<string, string[]>();
  for (const m of (members.data ?? []) as { user_id: string; role: string }[])
    rolesOf.set(m.user_id, [...(rolesOf.get(m.user_id) ?? []), m.role]);
  const pilotOnly = new Set([...rolesOf].filter(([, r]) => isPilotOnly(r)).map(([u]) => u));
  const l = lic.data as Record<string, unknown> | null;
  const deviceRows = (devices.data ?? []) as Record<string, unknown>[];
  return {
    licence: l
      ? {
          plan: String(l.plan),
          seats: num(l.seats),
          laptopsPerUser: num(l.laptops_per_user),
          mobilesPerUser: num(l.mobiles_per_user),
          features: (l.features as string[] | null) ?? [],
          status: l.status as Licence['status'],
          subscriptionEnds: str(l.subscription_ends),
          notes: String(l.notes ?? ''),
        }
      : null,
    seats: ((seats.data ?? []) as Record<string, unknown>[]).map((s) => {
      const u = one(s.app_user as UserEmbed | UserEmbed[]);
      return {
        id: String(s.id),
        userId: String(s.user_id),
        name: accountLabel(u),
        email: u?.email ?? null,
        takenAt: String(s.taken_at),
        lastCheckinAt: str(s.last_checkin_at),
        lastWebAt: str(s.last_web_at),
        devices: deviceRows
          .filter((d) => d.seat_id === s.id)
          .map((d) => ({
            id: String(d.id),
            kind: d.device_kind as LicenceDeviceKind,
            platform: str(d.device_platform),
            label: String(d.machine_label ?? ''),
            activatedAt: str(d.activated_at),
            lastCheckinAt: str(d.last_checkin_at),
            appVersion: str(d.app_version),
          }))
          .sort((a, b) =>
            a.kind === b.kind ? a.label.localeCompare(b.label) : a.kind === 'laptop' ? -1 : 1,
          ),
      };
    }),
    invoices: ((invoices.data ?? []) as Record<string, unknown>[]).map(invoiceFromRow),
    webCheck,
    minSyncVersion: minimum?.value ?? null,
    recommendedVersion: recommended?.version ?? null,
    pilotOnly,
  };
}

/** A tenant admin releases a seat or one device (decision 295); revoking is Braynewave's only. */
export function release(
  sb: SupabaseClient,
  what: { readonly seat: string } | { readonly device: string },
  note: string,
): Promise<void> {
  return endLicence(sb, what, 'released', note);
}

/**
 * The seat check at sign-in (licences spec §6.4): the database's answer, or null when it could not be asked (no
 * network, an older database without the function), which the domain's `webSignInGate` lets in.
 */
export async function webCheckin(
  sb: SupabaseClient,
  tenantId: string,
  now: Date = new Date(),
): Promise<WebCheckinAnswer | null> {
  try {
    const { data, error } = await sb.rpc('licence_web_checkin', { p_tenant: tenantId });
    if (error) return null;
    return parseWebCheckinAnswer(data, now);
  } catch {
    return null;
  }
}
